import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "@reach/router";
import { useModal } from "../../hooks/useModal";
import { useUser } from "../../hooks/useApi";
import { MarketingContext } from "./context";
import {
  emitMarketingEvent,
  MARKETING_EVENTS,
  subscribeMarketingEvent,
} from "./eventBus";
import {
  isMarketingConfigEnabled,
  MARKETING_FORMATS,
  MARKETING_PLACEMENTS,
} from "./normalizer";
import {
  buildMarketingStorageKey,
  canShowMarketingCreative,
  recordMarketingClick,
  recordMarketingClose,
  recordMarketingImpression,
} from "./storage";
import MarketingBannerSlot from "./MarketingBannerSlot";
import MarketingPopupOverlay from "./MarketingPopupOverlay";
import MarketingRouletteOverlay from "./MarketingRouletteOverlay";
import MarketingVideoOverlay from "./MarketingVideoOverlay";
import "./marketing.scss";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "touchstart",
  "keydown",
  "scroll",
];

const getDeviceCategory = () => {
  if (typeof window === "undefined") return "desktop";
  return window.matchMedia("(min-width: 768px)").matches ? "desktop" : "mobile";
};

const parseDateMs = (value) => {
  if (!value) return null;

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

export const isCampaignActiveForMarketing = (campaign, now, device) => {
  if (!campaign?.enabled) return false;
  if (Array.isArray(campaign.devices) && !campaign.devices.includes(device)) {
    return false;
  }

  const validFrom = parseDateMs(campaign.validFrom);
  const validTo = parseDateMs(campaign.validTo);

  if (validFrom && now < validFrom) return false;
  if (validTo && now > validTo) return false;

  return true;
};

const getItemTriggerDefinitions = (item) => {
  if (Array.isArray(item?.creative?.triggers) && item.creative.triggers.length > 0) {
    return item.creative.triggers;
  }

  return Array.isArray(item?.campaign?.triggers) ? item.campaign.triggers : [];
};

const getTriggerSignature = (trigger) =>
  [
    trigger?.type,
    trigger?.name ?? "",
    trigger?.placement ?? "",
    trigger?.percentage ?? "",
    trigger?.delaySeconds ?? "",
    trigger?.afterSeconds ?? "",
  ].join(":");

const sortByPriority = (items) =>
  [...items].sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return a.creative.id.localeCompare(b.creative.id);
  });

const getScrollDepth = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }

  const documentElement = document.documentElement;
  const scrollHeight = Math.max(
    documentElement.scrollHeight,
    document.body?.scrollHeight ?? 0
  );
  const viewportBottom = window.scrollY + window.innerHeight;

  if (!scrollHeight || scrollHeight <= window.innerHeight) return 100;

  return Math.min(100, Math.max(0, (viewportBottom / scrollHeight) * 100));
};

export default function MarketingProvider({
  children,
  config,
  radioid,
  nameseo,
  radioName,
  blocked = false,
}) {
  const navigate = useNavigate();
  const { user } = useUser();
  const { currentModal } = useModal(null);
  const userKey = user?.userid ?? user?.id ?? "anon";
  const configKey = `${config?.enabled ? "enabled" : "disabled"}:${
    config?.revision ?? "default"
  }`;
  const configKeyRef = useRef(configKey);
  const [stableConfig, setStableConfig] = useState(config);
  const runtimeReady =
    !blocked &&
    !!radioid &&
    isMarketingConfigEnabled(stableConfig);
  const providerKey = [
    radioid ?? "unscoped",
    userKey,
    stableConfig?.revision ?? "default",
    stableConfig?.enabled ? "enabled" : "disabled",
  ].join(":");

  const [selectedBanners, setSelectedBanners] = useState([]);
  const [dismissedBannerKeys, setDismissedBannerKeys] = useState(() => new Set());
  const [overlayQueue, setOverlayQueue] = useState([]);
  const [activeOverlay, setActiveOverlay] = useState(null);
  const activeOverlayRef = useRef(null);
  const triggeredKeysRef = useRef(new Set());
  const recordedImpressionsRef = useRef(new Set());

  useEffect(() => {
    if (configKeyRef.current !== configKey) {
      configKeyRef.current = configKey;
      setStableConfig(config);
    }
  }, [config, configKey]);

  useEffect(() => {
    activeOverlayRef.current = activeOverlay;
  }, [activeOverlay]);

  useEffect(() => {
    triggeredKeysRef.current = new Set();
    recordedImpressionsRef.current = new Set();
    setDismissedBannerKeys(new Set());
    setOverlayQueue([]);
    setActiveOverlay(null);
  }, [providerKey]);

  const buildItem = useCallback(
    (campaign, creative) => {
      const storageKey = buildMarketingStorageKey({
        radioid,
        userid: userKey,
        campaignId: campaign.id,
        creativeId: creative.id,
        revision: stableConfig?.revision,
      });

      return {
        key: `${campaign.id}:${creative.id}`,
        storageKey,
        campaign,
        creative,
        priority: Number(creative.priority ?? campaign.priority ?? 0),
        radio: {
          radioid,
          nameseo,
          radioname: radioName,
        },
      };
    },
    [nameseo, radioName, radioid, stableConfig?.revision, userKey]
  );

  const collectItems = useCallback(
    (format = null) => {
      if (!runtimeReady) return [];

      const now = Date.now();
      const device = getDeviceCategory();
      const items = [];

      stableConfig.campaigns.forEach((campaign) => {
        if (!isCampaignActiveForMarketing(campaign, now, device)) return;

        campaign.creatives.forEach((creative) => {
          if (format && creative.format !== format) return;
          items.push(buildItem(campaign, creative));
        });
      });

      return sortByPriority(items);
    },
    [buildItem, runtimeReady, stableConfig]
  );

  const canDisplayItem = useCallback((item) => {
    if (!item?.creative?.frequency || !item?.storageKey) return false;

    return canShowMarketingCreative({
      frequency: item.creative.frequency,
      key: item.storageKey,
      stopAfterClick: item.creative.stopAfterClick,
      stopAfterClickSeconds: item.creative.stopAfterClickSeconds,
    });
  }, []);

  useEffect(() => {
    if (!runtimeReady) {
      setSelectedBanners([]);
      return;
    }

    setSelectedBanners(
      collectItems(MARKETING_FORMATS.BANNER).filter((item) => canDisplayItem(item))
    );
  }, [canDisplayItem, collectItems, runtimeReady, providerKey]);

  const recordItemImpression = useCallback((item, payload = {}) => {
    if (!item?.storageKey || recordedImpressionsRef.current.has(item.storageKey)) {
      return;
    }

    recordedImpressionsRef.current.add(item.storageKey);
    recordMarketingImpression({
      frequency: item.creative.frequency,
      key: item.storageKey,
      payload: {
        format: item.creative.format,
        campaignId: item.campaign.id,
        creativeId: item.creative.id,
        ...payload,
      },
    });
  }, []);

  const recordItemClose = useCallback((item, reason = "closed") => {
    if (!item?.storageKey) return;

    recordMarketingClose({
      frequency: item.creative.frequency,
      key: item.storageKey,
      payload: {
        reason,
        format: item.creative.format,
        campaignId: item.campaign.id,
        creativeId: item.creative.id,
      },
    });
  }, []);

  const enqueueOverlay = useCallback(
    (item, trigger, payload = {}) => {
      if (!runtimeReady || !item || !trigger) return;

      const triggerKey = `${item.storageKey}:${getTriggerSignature(trigger)}`;
      if (triggeredKeysRef.current.has(triggerKey)) return;
      if (!canDisplayItem(item)) return;

      triggeredKeysRef.current.add(triggerKey);
      const queuedItem = {
        ...item,
        trigger,
        triggerPayload: payload,
      };

      setOverlayQueue((currentQueue) => {
        if (
          activeOverlayRef.current?.storageKey === item.storageKey ||
          currentQueue.some((queued) => queued.storageKey === item.storageKey)
        ) {
          return currentQueue;
        }

        return sortByPriority([...currentQueue, queuedItem]);
      });
    },
    [canDisplayItem, runtimeReady]
  );

  const collectOverlayItems = useCallback(
    () =>
      collectItems().filter((item) =>
        [
          MARKETING_FORMATS.POPUP,
          MARKETING_FORMATS.VIDEO,
          MARKETING_FORMATS.ROULETTE,
        ].includes(item.creative.format)
      ),
    [collectItems]
  );

  const scheduleTrigger = useCallback(
    (item, trigger, payload = {}, delaySeconds = trigger?.delaySeconds ?? 0) =>
      window.setTimeout(
        () => enqueueOverlay(item, trigger, payload),
        Math.max(0, Number(delaySeconds) || 0) * 1000
      ),
    [enqueueOverlay]
  );

  useEffect(() => {
    if (!runtimeReady || typeof window === "undefined") return undefined;

    const timers = [];
    collectOverlayItems().forEach((item) => {
      getItemTriggerDefinitions(item).forEach((trigger) => {
        if (trigger.type === "page_view") {
          timers.push(scheduleTrigger(item, trigger));
        }

        if (trigger.type === "time_on_page") {
          timers.push(
            scheduleTrigger(item, trigger, {}, trigger.afterSeconds ?? 0)
          );
        }
      });
    });

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [collectOverlayItems, providerKey, runtimeReady, scheduleTrigger]);

  useEffect(() => {
    if (!runtimeReady || typeof window === "undefined") return undefined;

    const idleTriggers = [];
    collectOverlayItems().forEach((item) => {
      getItemTriggerDefinitions(item).forEach((trigger) => {
        if (trigger.type === "idle") {
          idleTriggers.push({ item, trigger });
        }
      });
    });

    if (idleTriggers.length === 0) return undefined;

    let timers = [];
    const clearTimers = () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers = [];
    };
    const scheduleIdleTimers = () => {
      clearTimers();
      idleTriggers.forEach(({ item, trigger }) => {
        timers.push(
          scheduleTrigger(item, trigger, {}, trigger.afterSeconds ?? 0)
        );
      });
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, scheduleIdleTimers, { passive: true });
    });
    scheduleIdleTimers();

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, scheduleIdleTimers);
      });
    };
  }, [collectOverlayItems, providerKey, runtimeReady, scheduleTrigger]);

  useEffect(() => {
    if (!runtimeReady || typeof window === "undefined") return undefined;

    const scrollTriggers = [];
    collectOverlayItems().forEach((item) => {
      getItemTriggerDefinitions(item).forEach((trigger) => {
        if (trigger.type === "scroll_depth") {
          scrollTriggers.push({ item, trigger });
        }
      });
    });

    if (scrollTriggers.length === 0) return undefined;

    const handleScroll = () => {
      const currentDepth = getScrollDepth();
      scrollTriggers.forEach(({ item, trigger }) => {
        if (currentDepth >= trigger.percentage) {
          enqueueOverlay(item, trigger, { scrollDepth: currentDepth });
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [collectOverlayItems, enqueueOverlay, providerKey, runtimeReady]);

  useEffect(() => {
    if (!runtimeReady || typeof window === "undefined") return undefined;

    const timers = [];
    const unsubscribe = subscribeMarketingEvent(({ name, payload }) => {
      collectOverlayItems().forEach((item) => {
        getItemTriggerDefinitions(item).forEach((trigger) => {
          if (trigger.type === "event" && trigger.name === name) {
            timers.push(scheduleTrigger(item, trigger, payload));
          }

          if (
            trigger.type === "slot_visible" &&
            name === MARKETING_EVENTS.SLOT_VISIBLE &&
            (!trigger.placement || trigger.placement === payload?.placement)
          ) {
            timers.push(scheduleTrigger(item, trigger, payload));
          }
        });
      });
    });

    return () => {
      unsubscribe();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [collectOverlayItems, providerKey, runtimeReady, scheduleTrigger]);

  useEffect(() => {
    if (!runtimeReady) return;

    emitMarketingEvent(MARKETING_EVENTS.SONICBOX_LOADED, {
      radioid,
      nameseo,
      radioname: radioName,
    });
  }, [nameseo, radioName, radioid, runtimeReady, providerKey]);

  useEffect(() => {
    if (
      !runtimeReady ||
      activeOverlay ||
      currentModal !== null ||
      overlayQueue.length === 0
    ) {
      return;
    }

    const [nextOverlay, ...remainingQueue] = overlayQueue;
    setOverlayQueue(remainingQueue);

    if (!canDisplayItem(nextOverlay)) {
      return;
    }

    recordItemImpression(nextOverlay, {
      trigger: nextOverlay.trigger?.type,
      triggerName: nextOverlay.trigger?.name,
    });
    setActiveOverlay(nextOverlay);
  }, [
    activeOverlay,
    canDisplayItem,
    currentModal,
    overlayQueue,
    recordItemImpression,
    runtimeReady,
  ]);

  const closeOverlay = useCallback(
    (reason = "closed") => {
      const currentOverlay = activeOverlayRef.current;
      if (currentOverlay) {
        recordItemClose(currentOverlay, reason);
      }

      setActiveOverlay(null);
    },
    [recordItemClose]
  );

  useEffect(() => {
    if (activeOverlay && currentModal !== null) {
      closeOverlay("interrupted_by_modal");
    }
  }, [activeOverlay, closeOverlay, currentModal]);

  const getBannersForPlacement = useCallback(
    (placement, index = null) => {
      if (!runtimeReady || !placement) return [];

      return selectedBanners.filter((item) => {
        if (dismissedBannerKeys.has(item.storageKey)) return false;
        if (!item.creative.placements?.includes(placement)) return false;

        if (placement === MARKETING_PLACEMENTS.SONG_LIST_INLINE) {
          if (!index || !item.creative.inlineEvery) return false;
          return index % item.creative.inlineEvery === 0;
        }

        return true;
      });
    },
    [dismissedBannerKeys, runtimeReady, selectedBanners]
  );

  const recordBannerImpression = useCallback(
    (item, placement) => {
      recordItemImpression(item, { placement });
    },
    [recordItemImpression]
  );

  const dismissBanner = useCallback(
    (item) => {
      if (!item?.storageKey) return;

      setDismissedBannerKeys((current) => {
        const next = new Set(current);
        next.add(item.storageKey);
        return next;
      });
      recordItemClose(item, "banner_dismissed");
    },
    [recordItemClose]
  );

  const handleCreativeClick = useCallback(
    (item) => {
      const target = item?.creative?.target;
      const href = target?.href;
      if (!href) return;

      recordMarketingClick({
        frequency: item.creative.frequency,
        key: item.storageKey,
        payload: {
          format: item.creative.format,
          campaignId: item.campaign.id,
          creativeId: item.creative.id,
        },
      });

      if (href.startsWith("/") && target.openIn !== "new_tab") {
        navigate(href);
        return;
      }

      if (target.openIn === "new_tab") {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }

      window.location.assign(href);
    },
    [navigate]
  );

  const notifySlotVisible = useCallback((placement) => {
    emitMarketingEvent(MARKETING_EVENTS.SLOT_VISIBLE, { placement });
  }, []);

  const contextValue = useMemo(
    () => ({
      enabled: runtimeReady,
      getBannersForPlacement,
      recordBannerImpression,
      dismissBanner,
      handleCreativeClick,
      notifySlotVisible,
    }),
    [
      dismissBanner,
      getBannersForPlacement,
      handleCreativeClick,
      notifySlotVisible,
      recordBannerImpression,
      runtimeReady,
    ]
  );

  return (
    <MarketingContext.Provider value={contextValue}>
      {children}
      <MarketingBannerSlot
        placement={MARKETING_PLACEMENTS.FLOATING_ABOVE_PLAYER}
        maxItems={1}
      />
      {activeOverlay?.creative?.format === MARKETING_FORMATS.POPUP ? (
        <MarketingPopupOverlay
          item={activeOverlay}
          onClose={closeOverlay}
          onClick={handleCreativeClick}
        />
      ) : null}
      {activeOverlay?.creative?.format === MARKETING_FORMATS.VIDEO ? (
        <MarketingVideoOverlay
          item={activeOverlay}
          onClose={closeOverlay}
          onClick={handleCreativeClick}
        />
      ) : null}
      {activeOverlay?.creative?.format === MARKETING_FORMATS.ROULETTE ? (
        <MarketingRouletteOverlay item={activeOverlay} onClose={closeOverlay} />
      ) : null}
    </MarketingContext.Provider>
  );
}
